import React, { useEffect, useRef, useState } from 'react';
import { runStep, teardownOverlay } from '../utils/uiTestEngine';
import { LAB_SCRIPTS, prepareLabSandbox } from '../utils/labScripts';
import { setDialogAutoConfirm } from '../utils/dialog';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Global "Follow test" controller. Mounted once at the app shell so a run keeps
 * going as the robot navigates between screens (a panel inside Settings would
 * unmount the moment the run leaves Settings).
 *
 * Started via a window event:
 *   window.dispatchEvent(new CustomEvent('projectly_start_follow_test',
 *     { detail: { labId: 1, mode: 'auto' | 'manual' } }))
 *
 * Renders a floating narration card (top-right) with each action + ✓/✗, the
 * manual Next / Auto / Stop controls, and a copyable report at the end. Also
 * persists the result to the same key the Settings Test App panel reads.
 */
export default function FollowTestController() {
  const [active, setActive] = useState(false);
  const [steps, setSteps] = useState([]); // { say, ok, detail }
  const [banner, setBanner] = useState('');
  const [awaiting, setAwaiting] = useState(false);
  const [report, setReport] = useState('');
  const [copyMsg, setCopyMsg] = useState('');

  const modeRef = useRef('auto');
  const nextResolver = useRef(null);
  const stopRef = useRef(false);

  const gate = () => new Promise((res) => { nextResolver.current = res; });
  const advance = () => { const r = nextResolver.current; nextResolver.current = null; setAwaiting(false); r && r(); };
  const setModeBoth = (m) => { modeRef.current = m; };

  useEffect(() => {
    const onStart = (e) => {
      const labId = e?.detail?.labId ?? 1;
      const startMode = e?.detail?.mode || 'auto';
      run(labId, startMode);
    };
    window.addEventListener('projectly_start_follow_test', onStart);
    return () => window.removeEventListener('projectly_start_follow_test', onStart);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (labId, startMode) => {
    const script = LAB_SCRIPTS[labId];
    if (!script) return;
    stopRef.current = false;
    setModeBoth(startMode);
    setActive(true); setSteps([]); setReport(''); setCopyMsg('');
    const collected = [];
    // The lab Deploy button shows an in-app confirm; an in-app run can't click
    // its own modal, so auto-accept confirms for the duration of the run.
    setDialogAutoConfirm(true);
    try {
      setBanner('Preparing a clean Test Sandbox…');
      await prepareLabSandbox();
      for (const step of script.steps) {
        if (stopRef.current) { setBanner('Stopped.'); break; }
        setBanner(`${step.say}…`);
        let res;
        try { res = await runStep(step, { pause: 420 }); }
        catch (err) { res = { ok: false, detail: err.message }; }
        const entry = { say: step.say, ok: res.ok, detail: res.detail };
        collected.push(entry);
        setSteps((p) => [...p, entry]);
        setBanner(`${res.ok ? '✓' : '✗'} ${step.say}${res.detail && !res.ok ? ` — ${res.detail}` : ''}`);
        if (!res.ok) break; // stop on first failure
        if (modeRef.current === 'manual') { setAwaiting(true); await gate(); }
        else await sleep(550);
      }
      const failed = collected.filter((c) => !c.ok).length;
      const rep = buildReport(script.name, collected);
      setReport(rep);
      setBanner(failed === 0 ? '✅ All steps passed' : '❌ A step failed — see report');
      persist(script.name, collected, rep, failed);
    } catch (e) {
      setBanner(`Run error: ${e.message}`);
    } finally {
      setDialogAutoConfirm(false);
      teardownOverlay();
      setAwaiting(false);
    }
  };

  const stop = () => { stopRef.current = true; if (nextResolver.current) advance(); };
  const close = () => { setActive(false); setSteps([]); setReport(''); setBanner(''); };
  const copyReport = async () => {
    try { await navigator.clipboard.writeText(report); setCopyMsg('Copied!'); setTimeout(() => setCopyMsg(''), 1500); }
    catch { setCopyMsg('Select & copy'); }
  };

  if (!active) return null;

  return (
    <div style={{
      position: 'fixed', top: 14, right: 14, zIndex: 2147483645, width: 320, maxWidth: 'calc(100vw - 28px)',
      background: '#1e293b', color: '#e2e8f0',
      border: '1px solid #334155', borderRadius: 12,
      boxShadow: '0 10px 30px rgba(0,0,0,0.35)', padding: 12, fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong style={{ flex: 1 }}>🧪 Follow test</strong>
        <button onClick={stop} title="Stop" style={btnGhost}>■ Stop</button>
        {report && <button onClick={close} title="Close" style={btnGhost}>✕</button>}
      </div>

      <div style={{
        padding: '8px 10px', borderRadius: 8, marginBottom: 8, fontWeight: 600, lineHeight: 1.35,
        background: '#0f172a', borderLeft: '3px solid #6366f1',
        maxHeight: 80, overflow: 'auto',
      }}>{banner}</div>

      {awaiting && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <button onClick={advance} style={btnPrimary}>Next ▶</button>
          <button onClick={() => { setModeBoth('auto'); advance(); }} style={btnGhost}>Auto-run rest</button>
        </div>
      )}

      <div style={{ maxHeight: 180, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, fontSize: 12 }}>
            <span>{s.ok ? '✅' : '❌'}</span>
            <span style={{ flex: 1 }}>{s.say}{s.detail && !s.ok ? ` — ${s.detail}` : ''}</span>
          </div>
        ))}
      </div>

      {report && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={copyReport} style={btnGhost}>Copy report</button>
          {copyMsg && <span style={{ fontSize: 12, color: '#34d399' }}>{copyMsg}</span>}
        </div>
      )}
    </div>
  );
}

const btnGhost = {
  padding: '4px 10px', borderRadius: 6, border: '1px solid #334155',
  background: 'transparent', color: '#cbd5e1', cursor: 'pointer', fontSize: 12,
};
const btnPrimary = {
  padding: '4px 12px', borderRadius: 6, border: 'none',
  background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
};

function buildReport(name, steps) {
  const passed = steps.filter((s) => s.ok).length;
  const failed = steps.filter((s) => !s.ok).length;
  const lines = [
    'PROJECTLY — FOLLOW TEST REPORT',
    `Lab: ${name}`,
    `Run at: ${new Date().toLocaleString('en-GB')}`,
    `Result: ${failed === 0 ? 'ALL PASSED' : `${failed} FAILED`} (${passed} passed, ${failed} failed)`,
    '',
    ...steps.map((s) => `  ${s.ok ? '[PASS]' : '[FAIL]'} ${s.say}${s.detail && !s.ok ? ` — ${s.detail}` : ''}`),
    '',
    '(Ran in a disposable Test Sandbox workspace.)',
  ];
  return lines.join('\n');
}

function persist(name, steps, report, failed) {
  try {
    localStorage.setItem('projectly_testapp_last', JSON.stringify({
      results: steps.map((s) => ({ journey: name, label: s.say, status: s.ok ? 'pass' : 'fail', detail: s.detail })),
      report, banner: failed === 0 ? '✅ All steps passed' : '❌ A step failed', ranAt: Date.now(),
    }));
  } catch { /* ignore */ }
}
